import { SubscriptionInterval } from '../../__generated__/globalTypes';

/** Which checkout screen is showing; `card` is the fallback rail's entry. */
export type CheckoutScreen = 'plan' | 'account' | 'card' | 'authorize';

/** How the subscription will be funded. */
export type CheckoutRail = 'ach' | 'card';

export interface ICheckoutState {
  screen: CheckoutScreen;
  rail: CheckoutRail;
  selectedAccountId: string | null;
  selectedInterval: SubscriptionInterval;
  thirdPartyAck: boolean;
  /** The confirmed card setup the subscribe names; null until confirmed. */
  cardSetupIntentId: string | null;
}

export type CheckoutAction =
  | { type: 'selectAccount'; accountId: string }
  | { type: 'selectInterval'; interval: SubscriptionInterval }
  | { type: 'setThirdPartyAck'; acknowledged: boolean }
  | { type: 'payByCard' }
  | { type: 'cardConfirmed'; setupIntentId: string }
  | { type: 'advance' }
  | { type: 'back' }
  | { type: 'accountRemoved' };

/**
 * The state a fresh checkout opens with: the plan screen, the annual interval
 * preselected, no acknowledgment, and the given account id (or none)
 * selected.
 */
export function initialCheckoutState(preselectedAccountId: string | null): ICheckoutState {
  return {
    screen: 'plan',
    rail: 'ach',
    selectedAccountId: preselectedAccountId,
    // Annual is the pre-selected, discounted option.
    selectedInterval: SubscriptionInterval.Annual,
    thirdPartyAck: false,
    cardSetupIntentId: null,
  };
}

function forward(state: ICheckoutState): CheckoutScreen {
  switch (state.screen) {
    case 'plan':
      return 'account';
    case 'card':
      // The card screen advances through `cardConfirmed`, never blindly: the
      // setup id the next screens depend on only exists once Stripe confirms.
      return 'card';
    case 'account':
      return 'authorize';
    case 'authorize':
    default:
      return 'authorize';
  }
}

function backward(state: ICheckoutState): CheckoutScreen {
  switch (state.screen) {
    case 'plan':
      return 'plan';
    case 'account':
      return 'plan';
    case 'card':
      return 'account';
    case 'authorize':
    default:
      // The card rail rejoined here, so back retraces into it.
      return state.rail === 'card' ? 'card' : 'account';
  }
}

/**
 * Moves checkout between its screens, on either rail.
 *
 * Changing the account clears the acknowledgment. The disclosure names the
 * account it is about — it interpolates the mask — so carrying a `true` across
 * a change would store evidence claiming the subscriber acknowledged a
 * disclosure for an account they were never shown one for.
 *
 * Advancing from the account screen re-commits to the bank rail: a subscriber
 * who visited the card screen and came back is choosing to debit, and a stale
 * card setup id must not survive into that subscribe.
 */
export function checkoutReducer(state: ICheckoutState, action: CheckoutAction): ICheckoutState {
  switch (action.type) {
    case 'selectAccount':
      if (action.accountId === state.selectedAccountId) {
        return state;
      }
      return { ...state, selectedAccountId: action.accountId, thirdPartyAck: false };
    case 'selectInterval':
      return { ...state, selectedInterval: action.interval };
    case 'setThirdPartyAck':
      return { ...state, thirdPartyAck: action.acknowledged };
    case 'payByCard':
      return { ...state, screen: 'card', rail: 'card' };
    case 'cardConfirmed':
      return { ...state, screen: 'authorize', cardSetupIntentId: action.setupIntentId };
    case 'advance':
      if (state.screen === 'account') {
        return { ...state, screen: forward(state), rail: 'ach', cardSetupIntentId: null };
      }
      return { ...state, screen: forward(state) };
    case 'back':
      return { ...state, screen: backward(state) };
    case 'accountRemoved':
      // The selected account left the eligible list (a refetch dropped it).
      // Only the bank rail's authorization renders from it, so only that
      // screen would be stranded with nothing to draw — every other screen
      // keeps its place and just sheds the stale selection and ack. Snapping
      // from `plan` would push the subscriber past a choice they have not
      // made yet.
      return {
        ...state,
        screen: state.screen === 'authorize' && state.rail === 'ach' ? 'account' : state.screen,
        selectedAccountId: null,
        thirdPartyAck: false,
      };
    default:
      return state;
  }
}
